import React, { useState, useEffect } from 'react';
import { User, Permission, UserPermissions, RolePermissions } from '../interfaces/User';
import { userService } from '../services/userService';
import { Shield, Users, Save, RotateCcw, AlertCircle } from 'lucide-react';
import { Loading } from './';

interface PermissionManagerProps {
  user?: User;
  mode: 'user' | 'role';
  role?: string;
  onClose: () => void;
  onSave?: () => void;
}

const PermissionManager: React.FC<PermissionManagerProps> = ({ 
  user, 
  mode, 
  role, 
  onClose, 
  onSave 
}) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>(role || 'admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [user, selectedRole, mode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const allPermissions = await userService.getAllPermissions();
      setPermissions(allPermissions);

      if (mode === 'user' && user) {
        const userPerms = await userService.getUserPermissions(user.id);
        setUserPermissions(userPerms.permissions);
      } else if (mode === 'role') {
        const rolePerms = await userService.getRolePermissions(selectedRole);
        setRolePermissions(rolePerms.permissions);
      }
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
      setError('Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionId: string) => {
    if (mode === 'user') {
      setUserPermissions(prev => 
        prev.includes(permissionId) 
          ? prev.filter(id => id !== permissionId)
          : [...prev, permissionId]
      );
    } else {
      setRolePermissions(prev => 
        prev.includes(permissionId) 
          ? prev.filter(id => id !== permissionId)
          : [...prev, permissionId]
      );
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (mode === 'user' && user) {
        await userService.updateUserPermissions(user.id, userPermissions);
      } else if (mode === 'role') {
        await userService.updateRolePermissions(selectedRole, rolePermissions);
      }
      
      setError(null);
      onSave?.();
    } catch (err) {
      console.error('Erro ao salvar permissões:', err);
      setError('Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  const groupedPermissions = permissions.reduce((groups, permission) => {
    if (!groups[permission.category]) {
      groups[permission.category] = [];
    }
    groups[permission.category].push(permission);
    return groups;
  }, {} as Record<string, Permission[]>);

  const categoryLabels = {
    users: 'Usuários',
    emissions: 'Emissões',
    markups: 'Markups',
    settings: 'Configurações',
    dashboard: 'Dashboard'
  };

  const currentPermissions = mode === 'user' ? userPermissions : rolePermissions;

  if (loading) {
    return (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div className="card" style={{ minWidth: '400px' }}>
          <Loading text="Carregando permissões..." />
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="card" style={{ 
        minWidth: '600px', 
        maxWidth: '800px', 
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px' 
        }}>
          <h3 style={{ 
            margin: 0, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            <Shield size={20} />
            Gerenciar Permissões {mode === 'user' ? `- ${user?.username}` : `- Role: ${selectedRole}`}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px' }}>
            ×
          </button>
        </div>

        {error && (
          <div className="card" style={{
            backgroundColor: '#fee2e2',
            borderColor: '#fecaca',
            color: '#dc2626',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={16} />
              {error}
            </div>
          </div>
        )}

        {mode === 'role' && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-14)',
              fontWeight: 500,
              color: 'var(--color-dimgray)',
              marginBottom: '8px'
            }}>
              Selecionar Role:
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="input"
              style={{ width: '200px' }}
            >
              <option value="admin">Administrador</option>
              <option value="gestor">Gestor</option>
              <option value="emissor">Emissor</option>
            </select>
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
            <div key={category} style={{ marginBottom: '24px' }}>
              <h4 style={{ 
                marginBottom: '12px',
                color: 'var(--color-dimgray)',
                borderBottom: '1px solid var(--color-gray)',
                paddingBottom: '8px'
              }}>
                {categoryLabels[category as keyof typeof categoryLabels] || category}
              </h4>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '12px' 
              }}>
                {categoryPermissions.map(permission => (
                  <label 
                    key={permission.id} 
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      padding: '12px',
                      backgroundColor: 'var(--color-lightgray)',
                      borderRadius: 'var(--br-4)',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={currentPermissions.includes(permission.id)}
                      onChange={() => handlePermissionToggle(permission.id)}
                      style={{ marginTop: '2px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 500, 
                        fontSize: 'var(--font-size-14)',
                        marginBottom: '4px' 
                      }}>
                        {permission.name}
                      </div>
                      <div style={{ 
                        fontSize: 'var(--font-size-12)',
                        color: 'var(--color-darkgray)' 
                      }}>
                        {permission.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid var(--color-gray)',
          paddingTop: '20px'
        }}>
          <div style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-14)' }}>
            {currentPermissions.length} permissão(ões) selecionada(s)
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              className="btn btn-secondary"
              onClick={loadData}
              disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <RotateCcw size={16} />
              Resetar
            </button>
            
            <button 
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {saving ? (
                <>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionManager;